import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import NavigationProfessionnels from '@/components/NavigationProfessionnels';
import Footer from '@/components/Footer';
import FloatingFAQ from '@/components/FloatingFAQ';
import { ScrollReveal } from '@/components/ScrollReveal';
import { 
  Building,
  Users,
  Bike,
  CheckCircle,
  MapPin,
  Calendar,
  Clock,
  Star,
  Award,
  Shield,
  Zap,
  Heart,
  Target,
  Briefcase,
  Phone,
  Mail,
  Send,
  User,
  MessageSquare,
  TrendingUp,
  Settings,
  Wrench,
  Truck,
  ArrowRight,
  Package,
  Cog,
  UserCheck,
  Handshake
} from 'lucide-react';
import { IMAGES } from '@/assets/images';



const Professionnels = () => {
  return (
    <div className="min-h-screen bg-white">
      <NavigationProfessionnels />
      
      {/* HEADER SECTION */}
      <section className="bg-primary text-primary-foreground py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div>
                <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                  Solutions vélo pour les professionnels
                </h1>
                <div className="text-xl lg:text-2xl text-primary-foreground/90 mb-8 leading-relaxed space-y-4">
                  <p>
                    Les Hirondelles accompagne les campings, établissements touristiques et entreprises dans la mise en place de services vélo adaptés à leur activité.
                  </p>
                  <p>
                    Notre objectif : développer une activité vélo durable, performante et adaptée à chaque établissement.
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button asChild size="lg" className="bg-primary-foreground text-primary hover:bg-primary-foreground/90">
                    <Link to="/nos-services">
                      Découvrir nos solutions
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                  
                  <Button asChild size="lg" variant="outline" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                    <Link to="/nous-contacter">
                      Prendre contact
                    </Link>
                  </Button>
                </div>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div className="relative">
                <img 
                  src={IMAGES.CORPORATE_GROUP_CYCLING_20260211_111333_15}
                  alt="Solutions vélo professionnelles"
                  className="w-full h-96 object-cover rounded-2xl shadow-lg"
                />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* SECTION 1 – LOCATION LONGUE DURÉE POUR CAMPINGS */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-6">
                  Location longue durée de vélos
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  Nous proposons aux campings la location mensuelle de parcs de vélos adaptés à l'exploitation touristique. 
                  Les établissements exploitent directement leur parc vélo auprès de leurs clients.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-start">
                    <Calendar className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Durée de location flexible</h4>
                      <p className="text-slate-600">Minimum 3 mois, jusqu'à 7 mois pour couvrir une saison complète</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Package className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Parc personnalisé</h4>
                      <p className="text-slate-600">Chaque parc composé selon les besoins de l'établissement</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <TrendingUp className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Tarification simple</h4>
                      <p className="text-slate-600">Coût mensuel par vélo, transparent et prévisible</p>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 p-6 rounded-lg mb-6">
                  <h4 className="font-semibold text-slate-900 mb-3">Catalogue de vélos disponible :</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm text-slate-600">
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-emerald-600 mr-2" />
                      Vélos enfants
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-emerald-600 mr-2" />
                      Vélos adultes
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-emerald-600 mr-2" />
                      Vélos électriques
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-emerald-600 mr-2" />
                      VTT
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-emerald-600 mr-2" />
                      VTC
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-emerald-600 mr-2" />
                      Remorques
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-emerald-600 mr-2" />
                      Tandems
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-emerald-600 mr-2" />
                      Suiveurs
                    </div>
                  </div>
                </div>

                <Link to="/demande-saisonnier">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700">
                    Demander un devis location
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div className="relative">
                <img 
                  src={IMAGES.CAMPING_HERO_ILLUSTRATION_20260211_111332_11}
                  alt="Location longue durée vélos"
                  className="w-full h-auto rounded-2xl shadow-lg"
                />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* SECTION 2 – SOLUTION CLÉ EN MAIN */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div className="relative">
                <img 
                  src={IMAGES.WORKSHOP_PROFESSIONAL_20260211_111332_14}
                  alt="Exploitation complète service vélo"
                  className="w-full h-auto rounded-2xl shadow-lg"
                />
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-6">
                  Exploitation complète du service vélo
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  Les Hirondelles peut prendre en charge l'exploitation complète du service vélo au sein du camping. 
                  Notre équipe intervient directement sur place.
                </p>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start">
                    <Settings className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Mise en place du parc de vélos</h4>
                      <p className="text-slate-600">Installation et organisation complète</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <UserCheck className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Gestion du service client</h4>
                      <p className="text-slate-600">Accueil, conseil et assistance clientèle</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Wrench className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Entretien technique</h4>
                      <p className="text-slate-600">Maintenance préventive et réparations</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <MessageSquare className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Communication et PLV</h4>
                      <p className="text-slate-600">Supports de communication adaptés</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Calendar className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Système de réservation en ligne</h4>
                      <p className="text-slate-600">Outils de gestion et anticipation des réservations</p>
                    </div>
                  </div>
                </div>

                <div className="bg-emerald-50 p-6 rounded-lg mb-6">
                  <p className="text-emerald-800 font-medium">
                    Cette solution permet aux établissements de déléguer entièrement l'activité vélo.
                  </p>
                </div>

                <Link to="/nous-contacter">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700">
                    Découvrir cette solution
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* SECTION 3 – MISE À DISPOSITION DE PARC DE VÉLOS */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-6">
                  Mise à disposition de parc de vélos
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  Cette solution est particulièrement adaptée aux campings 1, 2 ou 3 étoiles. 
                  Les Hirondelles met à disposition un parc de vélos principalement issu du reconditionnement.
                </p>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start">
                    <Zap className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Aucun investissement initial</h4>
                      <p className="text-slate-600">Démarrage immédiat sans capital immobilisé</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Clock className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Parc immédiatement disponible</h4>
                      <p className="text-slate-600">Mise en service rapide</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <TrendingUp className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Grille tarifaire adaptable</h4>
                      <p className="text-slate-600">Adaptation selon l'ancienneté des vélos</p>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 p-6 rounded-lg mb-6">
                  <h4 className="font-semibold text-slate-900 mb-3">Vélos reconditionnés de qualité</h4>
                  <p className="text-slate-600">
                    Les vélos reconditionnés sont entièrement contrôlés et maintenus en parfait état de fonctionnement.
                  </p>
                </div>

                <Link to="/velos-occasion">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700">
                    Voir les vélos disponibles
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div className="relative">
                <img 
                  src={IMAGES.VELO_RECONDITIONNE_1}
                  alt="Parc vélos reconditionnés"
                  className="w-full h-auto rounded-2xl shadow-lg"
                />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* SECTION 4 – GROUPES ET SÉMINAIRES */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div className="relative">
                <img 
                  src={IMAGES.GROUPE_SEMINAIRE_1}
                  alt="Activités vélo groupes"
                  className="w-full h-auto rounded-2xl shadow-lg"
                />
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-6">
                  Organisation d'activités vélo pour groupes
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  Les Hirondelles organise des activités vélo pour entreprises, séminaires et groupes touristiques.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-start">
                    <Truck className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Livraison de vélos sur site</h4>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <MapPin className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Organisation de balades</h4>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Users className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Accompagnement des groupes</h4>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Target className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Création d'itinéraires personnalisés</h4>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Heart className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Activités autour de la découverte du territoire</h4>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-emerald-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-emerald-800 mb-2">Capacités</h4>
                    <p className="text-emerald-700 text-sm">Jusqu'à 60 vélos électriques</p>
                    <p className="text-emerald-700 text-sm">Jusqu'à 500 vélos classiques</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-slate-900 mb-2">Services complémentaires</h4>
                    <p className="text-slate-600 text-sm">Visites, dégustations, étapes touristiques, repas ou paniers pique-nique</p>
                  </div>
                </div>

                <Link to="/demande-groupe">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700">
                    Organiser un événement
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* SECTION 5 – BORNES D'AUTO-RÉPARATION */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-6">
                  Stations d'auto-réparation pour établissements
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  Nous proposons des bornes d'auto-réparation permettant aux clients d'effectuer les réparations simples.
                </p>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start">
                    <Zap className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Gonflage</h4>
                      <p className="text-slate-600">Station de gonflage accessible 24h/24</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Settings className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Réglage</h4>
                      <p className="text-slate-600">Outils pour ajustements simples</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Cog className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Petits ajustements mécaniques</h4>
                      <p className="text-slate-600">Réparations de base en autonomie</p>
                    </div>
                  </div>
                </div>

                <div className="bg-emerald-50 p-6 rounded-lg mb-6">
                  <p className="text-emerald-800 font-medium">
                    Ces stations permettent de renforcer l'offre de mobilité douce dans les établissements.
                  </p>
                </div>

                <Link to="/borne-autoreparation">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700">
                    Installer une borne
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div className="relative">
                <img 
                  src={IMAGES.BIKE_STATION_HERO_20260211_111335_12}
                  alt="Borne auto-réparation"
                  className="w-full h-auto rounded-2xl shadow-lg"
                />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* SECTION 6 – ACCOMPAGNEMENT À L'ACQUISITION DE VÉLOS */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div className="relative">
                <img 
                  src={IMAGES.VELO_VILLE_CLASSIQUE_RECONDITIONNE_20260211_125242_23}
                  alt="Acquisition parcs vélos"
                  className="w-full h-auto rounded-2xl shadow-lg"
                />
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-6">
                  Acquisition de parcs de vélos
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  Les Hirondelles accompagne les établissements souhaitant acquérir leur propre parc de vélos. 
                  Nous proposons des modèles conçus pour l'exploitation locative.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-start">
                    <Shield className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Durabilité</h4>
                      <p className="text-slate-600">Vélos optimisés pour l'usage intensif</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Wrench className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Entretien simplifié</h4>
                      <p className="text-slate-600">Maintenance facilitée</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Award className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Résistance à l'usage intensif</h4>
                      <p className="text-slate-600">Conçus pour la location</p>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 p-6 rounded-lg mb-6">
                  <h4 className="font-semibold text-slate-900 mb-3">Composants optimisés :</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-slate-600">
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-emerald-600 mr-2" />
                      Cadres renforcés
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-emerald-600 mr-2" />
                      Pièces aluminium
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-emerald-600 mr-2" />
                      Pneus anti-crevaison
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-emerald-600 mr-2" />
                      Composants location
                    </div>
                  </div>
                </div>

                <div className="bg-emerald-50 p-6 rounded-lg mb-6">
                  <h4 className="font-semibold text-emerald-800 mb-3">Nos propres vélos enfants :</h4>
                  <div className="flex space-x-4">
                    <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">Modèles 20 pouces</span>
                    <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">Modèles 24 pouces</span>
                  </div>
                </div>

                <Link to="/achat-parcs-velo">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700">
                    Découvrir notre catalogue
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* SECTION 7 – FACILITATEUR D'ACTIVITÉ */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-6">
                  Facilitateur d'activité vélo
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  Notre métier consiste à accompagner le développement d'activités vélo dans les établissements touristiques.
                </p>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start">
                    <Target className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Solutions adaptées</h4>
                      <p className="text-slate-600">Nous adaptons nos solutions aux besoins spécifiques de chaque site</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Handshake className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Accompagnement personnalisé</h4>
                      <p className="text-slate-600">Développement d'activités vélo sur mesure</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <MapPin className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Mobilité interne</h4>
                      <p className="text-slate-600">Mise en place de solutions de mobilité interne</p>
                    </div>
                  </div>
                </div>

                <Link to="/nous-contacter">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700">
                    Discuter de votre projet
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div className="relative">
                <img 
                  src={IMAGES.DELIVERY_SERVICE_ILLUSTRATION_20260211_111332_13}
                  alt="Facilitateur activité vélo"
                  className="w-full h-auto rounded-2xl shadow-lg"
                />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* SECTION 8 – PARCS DE VÉLOS POUR LES ÉQUIPES */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div className="relative">
                <img 
                  src={IMAGES.TEAM_CYCLING_1}
                  alt="Mobilité équipes campings"
                  className="w-full h-auto rounded-2xl shadow-lg"
                />
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-6">
                  Mobilité interne pour les équipes
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  Les Hirondelles propose également des parcs de vélos destinés aux équipes des campings.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-start">
                    <Wrench className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Personnel technique</h4>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <User className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Équipes de ménage</h4>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Users className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Saisonniers</h4>
                    </div>
                  </div>
                </div>

                <div className="bg-emerald-50 p-6 rounded-lg mb-6">
                  <h4 className="font-semibold text-emerald-800 mb-3">Objectif :</h4>
                  <p className="text-emerald-700">
                    Faciliter les déplacements internes sur les grands campings.
                  </p>
                </div>

                <div className="bg-slate-50 p-6 rounded-lg mb-6">
                  <h4 className="font-semibold text-slate-900 mb-3">Équipements spécifiques disponibles :</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-slate-600">
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-emerald-600 mr-2" />
                      Remorques
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-emerald-600 mr-2" />
                      Vélos utilitaires
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-emerald-600 mr-2" />
                      Accessoires pro
                    </div>
                  </div>
                </div>

                <Link to="/nous-contacter">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700">
                    Équiper vos équipes
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* SECTION FINALE - CTA */}
      <section className="py-20 bg-emerald-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h2 className="text-3xl font-bold mb-6">
              Prêt à développer votre activité vélo ?
            </h2>
            <p className="text-xl text-emerald-100 mb-8 leading-relaxed">
              Contactez-nous pour discuter de vos besoins et découvrir la solution adaptée à votre établissement.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/nous-contacter">
                <Button size="lg" variant="secondary" className="bg-white text-emerald-600 hover:bg-emerald-50">
                  Prendre contact
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/nos-services">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-emerald-600">
                  Découvrir nos services
                </Button>
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <Footer />
      <FloatingFAQ />
    </div>
  );
};

export default Professionnels;